import "@/../css/emotes.css";

type SpriteLayout = {
    columnsPerSide: number;
    rowsPerSide: number;
    sideInsetPercent: number;
    sideWidthPercent: number;
    topPercent: number;
    bottomPercent: number;
};

type SpriteEmoteConfig = {
    imagePath: string;
    motion: "rise" | "burst";
    layout: SpriteLayout;
    maxDelayMs: number;
};

type Side = "left" | "right";

const RISING_LAYOUT: SpriteLayout = {
    columnsPerSide: 2,
    rowsPerSide: 4,
    sideInsetPercent: 2,
    sideWidthPercent: 22,
    topPercent: 12,
    bottomPercent: 80,
};

export const SPRITE_EMOTES = {
    applause: {
        imagePath: "/images/emotes/applause.svg",
        motion: "rise",
        layout: RISING_LAYOUT,
        maxDelayMs: 1_400,
    },
    fire: {
        imagePath: "/images/emotes/fire.svg",
        motion: "rise",
        layout: { ...RISING_LAYOUT, topPercent: 20, bottomPercent: 86 },
        maxDelayMs: 1_400,
    },
    fireworks: {
        imagePath: "/images/emotes/fireworks.webp",
        motion: "burst",
        layout: {
            columnsPerSide: 2,
            rowsPerSide: 6,
            sideInsetPercent: 2,
            sideWidthPercent: 26,
            topPercent: 12,
            bottomPercent: 80,
        },
        maxDelayMs: 2_000,
    },
} satisfies Record<string, SpriteEmoteConfig>;

export type SpriteEmote = keyof typeof SPRITE_EMOTES;

export const SPRITE_ANIMATION_MS = 2_400;

const SPRITE_WIDTH_PERCENT = 6;
const MAX_DRIFT_VW = 3;

export function isSpriteEmote(value: unknown): value is SpriteEmote {
    return typeof value === "string" && Object.hasOwn(SPRITE_EMOTES, value);
}

export function spritesPerSide(emote: SpriteEmote) {
    const { columnsPerSide, rowsPerSide } = SPRITE_EMOTES[emote].layout;
    return columnsPerSide * rowsPerSide;
}

function spriteUrl(emote: SpriteEmote) {
    return `${import.meta.env.VITE_CDN_URL}${SPRITE_EMOTES[emote].imagePath}`;
}

function randomBetween(min: number, max: number) {
    return min + Math.random() * (max - min);
}

export function preloadEmoteSprites() {
    for (const emote of Object.keys(SPRITE_EMOTES)) {
        if (isSpriteEmote(emote)) {
            new Image().src = spriteUrl(emote);
        }
    }
}

function sideZoneStart(layout: SpriteLayout, side: Side) {
    return side === "left"
        ? layout.sideInsetPercent
        : 100 -
              SPRITE_WIDTH_PERCENT -
              layout.sideInsetPercent -
              layout.sideWidthPercent;
}

function shuffledDelays(count: number, maxDelayMs: number) {
    const slotMs = maxDelayMs / count;
    const delays = Array.from({ length: count }, (_, slot) =>
        Math.round((slot + Math.random()) * slotMs),
    );
    for (let index = delays.length - 1; index > 0; index--) {
        const swapIndex = Math.floor(Math.random() * (index + 1));
        [delays[index], delays[swapIndex]] = [delays[swapIndex], delays[index]];
    }
    return delays;
}

function createSprite(
    emote: SpriteEmote,
    { x, y, delayMs }: { x: number; y: number; delayMs: number },
): HTMLImageElement {
    const sprite = document.createElement("img");
    sprite.src = spriteUrl(emote);
    sprite.alt = "";
    sprite.className = `emote-sprite emote-sprite-${SPRITE_EMOTES[emote].motion}`;
    sprite.dataset.testid = "emote-sprite";

    sprite.style.setProperty("--emote-x", `${x}%`);
    sprite.style.setProperty("--emote-y", `${y}%`);
    sprite.style.setProperty(
        "--emote-drift",
        `${randomBetween(-MAX_DRIFT_VW, MAX_DRIFT_VW)}vw`,
    );
    sprite.style.setProperty("--emote-delay", `${delayMs}ms`);
    sprite.style.setProperty("--emote-duration", `${SPRITE_ANIMATION_MS}ms`);

    return sprite;
}

export default function playSpriteEmote(emote: SpriteEmote) {
    const layer = document.createElement("div");
    layer.className = "emote-layer";
    layer.setAttribute("aria-hidden", "true");
    layer.dataset.testid = "emote-layer";

    const { layout, maxDelayMs } = SPRITE_EMOTES[emote];
    const cellWidth = layout.sideWidthPercent / layout.columnsPerSide;
    const cellHeight =
        (layout.bottomPercent - layout.topPercent) / layout.rowsPerSide;
    const delays = shuffledDelays(spritesPerSide(emote) * 2, maxDelayMs);
    let spriteIndex = 0;

    for (const side of ["left", "right"] satisfies Side[]) {
        const zoneStart = sideZoneStart(layout, side);
        for (let row = 0; row < layout.rowsPerSide; row++) {
            for (let column = 0; column < layout.columnsPerSide; column++) {
                const cellLeft = zoneStart + column * cellWidth;
                const cellTop = layout.topPercent + row * cellHeight;
                layer.append(
                    createSprite(emote, {
                        x: randomBetween(cellLeft, cellLeft + cellWidth),
                        y: randomBetween(cellTop, cellTop + cellHeight),
                        delayMs: delays[spriteIndex++],
                    }),
                );
            }
        }
    }

    document.body.append(layer);
    window.setTimeout(() => layer.remove(), SPRITE_ANIMATION_MS + maxDelayMs);
}
